import { useState } from "react";
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  increment,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { analytics, db } from "../firebase-config"; // Adjust path
import { logEvent } from "firebase/analytics";
import { sanitizeInput } from "../utils/inputSanitizer"; // Adjust path
import { generateShortCode } from "../utils/firestoreUtils"; // Adjust path
import useDateUtils from "../utils/fromStringToTimestamp"; // Adjust path
import { useToast } from "../components/Alerts"; // Adjust path
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
// No need to import getAuth if currentUser is already the auth user object

// Constants for Limits (consider moving to a config file)
const UNLIMITED_APPS = 999999; // Use a large number for "unlimited"
const DEFAULT_ANALYSIS_QUOTA = 30000; // Default quota for plans with analysis
const BASIC_APP_LIMIT = 1000;
const FREE_APP_LIMIT = 10; // Based on previous modal description
const FREE_ANALYSIS_QUOTA = 0; // No AI analysis for free tier

export const useCreateJob = ({ currentUser, userData, companyData, onSuccess, onError }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { stringToTimestamp } = useDateUtils();
  const { addToast } = useToast();
  // Assuming 'useCreateJob' namespace for translations
  const { t } = useTranslation("useCreateJob");

  // --- Function to Enqueue AI Assessment ---
  // (No changes needed here based on the license update itself,
  // but the *call* to this function will be updated later)
   const enqueueAssessmentGeneration = async (jobPostingForApi, userAuth) => {
       if (!userAuth) {
           console.warn("Cannot enqueue assessment: currentUser (auth object) is missing.");
           addToast({ type: "warning", message: t("assessmentEnqueueWarningAuth") });
           return;
       }
       const apiUrl = process.env.REACT_APP_ENQUEUE_ASSESSMENT_URL;
       if (!apiUrl) {
           console.error("Assessment enqueue URL (REACT_APP_ENQUEUE_ASSESSMENT_URL) is not configured.");
           addToast({ type: "warning", message: t("assessmentEnqueueWarningConfig") });
           return;
       }
       console.log("Attempting to enqueue assessment generation for job:", jobPostingForApi.uuid);
       let idToken;
       try {
           idToken = await userAuth.getIdToken(true);
       } catch (authError) {
           console.error("Error getting Firebase ID token for assessment enqueue:", authError);
           addToast({ type: "error", message: t("assessmentEnqueueErrorAuth") });
           Sentry.captureException(authError);
           return;
       }
       try {
           const response = await fetch(apiUrl, {
               method: 'POST',
               headers: {
                   'Content-Type': 'application/json',
                   'Authorization': `Bearer ${idToken}`,
               },
               body: JSON.stringify(jobPostingForApi),
           });
           const responseData = await response.json();
           if (!response.ok) {
               console.error(`Failed to enqueue assessment. Status: ${response.status}`, responseData);
               Sentry.captureMessage(`Assessment enqueue failed with status ${response.status}`, {
                   extra: { responseData, jobId: jobPostingForApi.uuid },
                   level: "warning",
               });
               addToast({ type: "warning", message: `${t("assessmentEnqueueErrorApi")}: ${responseData.message || response.statusText}` });
           } else if (response.status === 202) {
               console.log("Successfully enqueued assessment generation task:", responseData);
               addToast({ type: "info", message: t("assessmentEnqueueSuccess") });
           } else {
               console.warn("Unexpected response status from assessment enqueue endpoint:", response.status, responseData);
               addToast({ type: "info", message: t("assessmentEnqueueInfoUnexpected") });
           }
       } catch (fetchError) {
           console.error("Error calling assessment enqueue endpoint:", fetchError);
           Sentry.captureException(fetchError);
           addToast({ type: "error", message: `${t("assessmentEnqueueErrorNetwork")}: ${fetchError.message}` });
       }
   };
   // --- End of Enqueue Function ---


  const createJob = async (event) => {
    event.preventDefault(); // Prevent default form submission

    // Initial validation
    if (!currentUser) {
      console.error("Missing currentUser (auth object) for job creation.");
      addToast({ type: "error", message: t("createJobErrorMissingAuth") });
      if (onError) onError(new Error(t("errorMessages.missingAuth"))); // Use translated message
      return;
    }
    if (!userData || !companyData || companyData.length === 0) {
      console.error("Missing userData or companyData for job creation.");
      addToast({ type: "error", message: t("createJobErrorMissingData") });
      if (onError) onError(new Error(t("errorMessages.missingData"))); // Use translated message
      return;
    }

    setIsLoading(true);
    let jobPosting; // Define scope

    try {
      // Extract form data (assuming event target is the form)
      const formData = new FormData(event.target);
      const newJobDataObject = Object.fromEntries(formData.entries());

      // Add assessments if passed directly (e.g., from react-select in modal state)
      // Assuming the modal state 'data' is merged or accessible
      // We need the selected license type *before* sanitizing if it's not a standard form field
       const selectedJobOfferType = newJobDataObject.jobOfferType || event.target.elements.jobOfferType?.value || 'Free'; // Get license type reliably
       const selectedAssessments = event.target.assessments?.value || null; // Example if assessments passed directly

      const sanitizedJobData = Object.fromEntries(
        Object.entries(newJobDataObject).map(([key, value]) => [
          key,
          typeof value === 'string' ? sanitizeInput(value) : value,
        ])
      );
      // Re-add selectedJobOfferType and assessments after sanitization if needed
       sanitizedJobData.jobOfferType = selectedJobOfferType;
       if (selectedAssessments) {
            sanitizedJobData.assessments = selectedAssessments;
       }


      logEvent(analytics, "select_content", {
        content_type: "handleCreateJobSubmit",
        content_id: sanitizedJobData?.Titre || "Untitled Job",
      });

      const docRef = doc(
        collection(db, `job-descriptions/${userData.tenantId}/active`)
      );

      const applicationUrlId = await generateShortCode(docRef);
      if (!applicationUrlId) {
        throw new Error(t("errorMessages.generateUrlIdFailed")); // Use translated message
      }

      // --- Determine License Type and Decrement ---
      let licenseUpdateField = null; // Field to decrement (e.g., basicLicense)
      let jobType = "free"; // Final determined job type for limits/features
      const currentCompanyData = companyData[0];
      const jobOfferTypeLower = sanitizedJobData.jobOfferType.toLowerCase();

      if (jobOfferTypeLower.includes("basic")) {
        if ((currentCompanyData?.basicLicense ?? 0) <= 0) throw new Error(t("errorMessages.noBasicLicenses"));
        licenseUpdateField = "basicLicense";
        jobType = "basic";
      } else if (jobOfferTypeLower.includes("growth")) { // **** ADDED GROWTH ****
        if ((currentCompanyData?.growthLicense ?? 0) <= 0) throw new Error(t("errorMessages.noGrowthLicenses")); // Add translation
        licenseUpdateField = "growthLicense"; // Assuming field name
        jobType = "growth";
      } else if (jobOfferTypeLower.includes("pro")) { // 'pro' for Professional
        if ((currentCompanyData?.proLicense ?? 0) <= 0) throw new Error(t("errorMessages.noProLicenses"));
        licenseUpdateField = "proLicense";
        jobType = "pro";
      } else if (jobOfferTypeLower.includes("premium")) { // **** ADDED PREMIUM ****
        if ((currentCompanyData?.premiumLicense ?? 0) <= 0) throw new Error(t("errorMessages.noPremiumLicenses")); // Add translation
        licenseUpdateField = "premiumLicense"; // Assuming field name
        jobType = "premium";
      } else { // Free / Trial
        if (!currentCompanyData.hasUsedFreeTrial && (currentCompanyData?.trialLicense ?? 0) > 0) {
          // Use trial license if available and not used
          licenseUpdateField = "trialLicense"; // Decrement trial if tracked this way
           // Need to mark trial as used separately
        } else if (currentCompanyData.hasUsedFreeTrial) {
           // Trial already used, allow free job without decrement (or throw error if free jobs not allowed)
           console.warn("Free trial already used or unavailable, creating free job without license decrement.");
        } else {
           // No trial license available, and hasn't used free trial -> Should not happen if UI prevents this state?
           // Or, if free jobs are allowed anytime, just proceed. For now, assume trial required if not used.
           throw new Error(t("errorMessages.noFreeTrial"));
        }
        jobType = "free";
      }

      // --- Prepare Firestore Updates ---
      // Common updates for all job types
      const commonUpdates = {
        "jobCreatedCount": increment(1),
        "lastActivityDate": Timestamp.now(),
      };
      // Specific license decrement or trial usage update
      let licenseSpecificUpdate = {};
      if (licenseUpdateField && jobType !== 'free') { // Decrement paid licenses
          licenseSpecificUpdate[licenseUpdateField] = increment(-1);
          // Mark paid license as used if not already
          if (!currentCompanyData.hasUsedPaidLicense) {
             licenseSpecificUpdate["hasUsedPaidLicense"] = true;
          }
      } else if (jobType === 'free' && !currentCompanyData.hasUsedFreeTrial) { // Mark free trial as used
          licenseSpecificUpdate["hasUsedFreeTrial"] = true;
          // Decrement trialLicense if tracked and applicable
          if (licenseUpdateField === "trialLicense") {
             licenseSpecificUpdate[licenseUpdateField] = increment(-1);
          }
      }

       const finalLicenseUpdateJson = { ...commonUpdates, ...licenseSpecificUpdate };


      // --- Set Limits/Quotas Based on FINAL jobType ---
      let resumeLimit;
      let resumeAnalysisQuota;

      switch (jobType) {
          case "basic":
              resumeLimit = BASIC_APP_LIMIT;
              resumeAnalysisQuota = 0; // Basic doesn't mention AI analysis
              break;
          case "growth":
              resumeLimit = UNLIMITED_APPS;
              resumeAnalysisQuota = DEFAULT_ANALYSIS_QUOTA; // Growth has CV analysis
              break;
          case "pro":
              resumeLimit = UNLIMITED_APPS;
              resumeAnalysisQuota = DEFAULT_ANALYSIS_QUOTA; // Pro includes Growth features + Assessments
              break;
          case "premium":
              resumeLimit = UNLIMITED_APPS;
              resumeAnalysisQuota = DEFAULT_ANALYSIS_QUOTA; // Premium includes Pro features + Sourcing
              break;
          case "free":
          default: // Default to free/trial limits
              resumeLimit = FREE_APP_LIMIT;
              resumeAnalysisQuota = FREE_ANALYSIS_QUOTA;
              break;
      }

      // --- Construct Job Posting Object ---
      jobPosting = {
        assessments: sanitizedJobData.assessments || [], // Ensure it's an array
        licenseType: jobType, // Store the final determined type
        type: jobType, // Duplicate for compatibility? Decide if needed.
        JobID: applicationUrlId,
        uuid: docRef.id,
        tenantId: userData.tenantId,
        resumeAnalysisQuota: resumeAnalysisQuota, // Use calculated quota
        resumeLimit: resumeLimit, // Use calculated limit
        Titre: sanitizedJobData.Titre || "",
        Entreprise: sanitizedJobData.Entreprise || currentCompanyData?.companyName || "",
        // ... (Keep other fields as they are, ensure sanitization is applied) ...
        EntrepriseDescription: sanitizedJobData.DescriptionDeLentreprise || currentCompanyData?.description || "",
        Lieu: sanitizedJobData.Lieu || "",
        TypeDePoste: sanitizedJobData.TypeDePoste || "",
        Secteur: sanitizedJobData.Secteur || "",
        CategorieDeRole: sanitizedJobData.CategorieDeRole || "",
        Description: sanitizedJobData.Description || "",
        Qualifications: sanitizedJobData.Qualifications || "",
        NiveauDExperience: sanitizedJobData.NiveauDExperience || "",
        CompetencesRequises: sanitizedJobData.CompetencesRequises?.split(",").map(s => s.trim()).filter(Boolean) || [],
        additionalInformation: sanitizedJobData.additionalInformation || "",
        Avantages: sanitizedJobData.Avantages?.split(",").map(s => s.trim()).filter(Boolean) || [],
        DateLimiteDeCandidature: stringToTimestamp(sanitizedJobData.DateLimiteDeCandidature),
        PublicationDate: stringToTimestamp(sanitizedJobData.DateDePublication) || serverTimestamp(),
        ProcedureDeCandidature: "",
        minimumRelevantYearsOfExperience: sanitizedJobData.minimumRelevantYearsOfExperience || "0",
        linkToApply: `${process.env.REACT_APP_BASE_URL || window.location.origin}/jobs/${applicationUrlId}`,
        Responsibilities: sanitizedJobData.Responsibilities || "",
        InformationsDeContact: {
          NomDuContactRH: sanitizedJobData.NomDuContactRH || "",
          Email: sanitizedJobData.Email || userData.email || "",
          Telephone: sanitizedJobData.Telephone || "",
        },
        CreatedBy: {
          fullName: userData.displayName || userData.email || "Unknown User",
          email: userData.email,
          uuid: userData.uuid || currentUser.uid, // Use currentUser.uid as fallback
        },
        createdOn: serverTimestamp(),
        updatedOn: serverTimestamp(),
        Statut: "Submissions", // Default status
        language: sanitizedJobData.language || navigator.language.split('-')[0] || "en",
      };

      // --- Perform Firestore operations ---
      await setDoc(docRef, jobPosting);
      console.log("Job posting saved to Firestore:", docRef.id);

      if (Object.keys(finalLicenseUpdateJson).length > 0) {
        const companyRef = doc(db, `company`, userData.tenantId);
        await updateDoc(companyRef, finalLicenseUpdateJson);
        console.log("Company license/stats updated:", finalLicenseUpdateJson);
      }

      // --- Enqueue Assessment if Job Type is Pro or Premium --- // **** UPDATED CONDITION ****
       if (jobType === "pro" || jobType === "premium") {
         console.log(`Job type is ${jobType}, enqueuing assessment.`);
         await enqueueAssessmentGeneration(jobPosting, currentUser); // Pass auth object
       }
      // --- End Enqueue Logic ---


      logEvent(analytics, "createdJobPosting", {
        tenantId: userData.tenantId,
        jobId: jobPosting.uuid,
        jobType: jobPosting.licenseType, // Use consistent field
        email: userData.email,
      });

      addToast({ type: "success", message: t("jobCreatedSuccess") });
      setIsLoading(false);
      if (onSuccess) onSuccess(jobPosting); // Pass created job data back

    } catch (error) {
      console.error("Error during job creation process:", error);
      if (process.env.REACT_APP_ENVIRONEMENT === "prod") { Sentry.captureException(error); }
      addToast({ type: "error", message: `${t("createJobError")}: ${error.message}` });
      setIsLoading(false);
      if (onError) onError(error);
    }
  };

  return { createJob, isLoading };
};